'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { CheckCircle, ShoppingBag, ArrowLeft, X, Info, FileText } from 'lucide-react';
import { useQuotation } from '@/app/context/quotation-context';
import { useAuth } from '@clerk/nextjs';

export default function QuotationConfirmation() {
  const { showConfirmation, setShowConfirmation, quoteNumber, formData } = useQuotation();
  const router = useRouter();
  const [navigating, setNavigating] = useState(false);
  const { isSignedIn } = useAuth();

  // Reset navigating state when modal is closed
  useEffect(() => {
    if (!showConfirmation) {
      setNavigating(false);
    }
  }, [showConfirmation]);
  
  // Smooth navigation with proper state management
  const handleNavigate = (path: string) => {
    if (navigating) return;
    
    setNavigating(true);
    
    // Smooth transition
    setTimeout(() => {
      setShowConfirmation(false);
      setTimeout(() => {
        router.push(path);
      }, 300);
    }, 200);
  };
  
  // Handle close button
  const handleClose = () => {
    setShowConfirmation(false);
  };
  
  if (!showConfirmation) return null;
  
  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black transition-opacity duration-300 ${
      showConfirmation ? 'bg-opacity-50' : 'bg-opacity-0'
    }`}>
      <div className={`bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 ${
        showConfirmation ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}>
        {/* Header */}
        <div className="relative px-4 sm:px-6 py-6 sm:py-8 bg-blue-50 border-b border-blue-100 text-center">
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-white/50"
            aria-label="Cerrar confirmación"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-blue-500 mx-auto mb-4" />
          <h1 className="text-xl sm:text-2xl font-bold text-blue-700 mb-2">¡Solicitud de Cotización Enviada!</h1>
          <p className="text-sm sm:text-base text-blue-600">
            Tu solicitud ha sido recibida exitosamente. Nuestro equipo de ventas te contactará pronto.
          </p>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Quote details */}
          <div className="mb-8">
            <h2 className="text-lg font-medium mb-4 border-b pb-2">Detalles de la Solicitud</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Número de Cotización</p>
                <p className="text-gray-800 font-semibold">{quoteNumber || 'Procesando...'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Fecha</p>
                <p className="text-gray-800">
                  {new Date().toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>
          
          {/* Process timeline */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="text-2xl mr-3">📋</div>
              <div>
                <h3 className="font-medium text-yellow-800 mb-2">¿Qué sigue?</h3>
                <ol className="text-yellow-700 text-sm space-y-1 mb-3 list-decimal pl-4">
                  <li><strong>Revisión:</strong> Analizaremos tu solicitud (1-2 días hábiles)</li>
                  <li><strong>Cotización:</strong> Prepararemos una propuesta personalizada</li>
                  <li><strong>Contacto:</strong> Te llamaremos para discutir los detalles</li>
                  <li><strong>Aprobación:</strong> Una vez aprobada, podrás proceder con el pedido</li>
                </ol>
              </div>
            </div>
          </div>
          
          {/* Guest checkout info */}
          {!isSignedIn && (
            <div className="mb-6 p-3 bg-amber-50 border border-amber-100 rounded-lg flex items-start">
              <Info className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-amber-800 mb-1">Solicitud como invitado</h4>
                <p className="text-amber-700 text-sm">
                  Has enviado esta cotización como invitado. Para ver todas tus cotizaciones y tener un seguimiento completo, considera crear una cuenta o iniciar sesión.
                </p>
              </div>
            </div>
          )}
          
          {/* Contact information */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-800 mb-2">Información de Contacto</h3>
            <p className="text-gray-600 text-sm">
              <strong>Nombre:</strong> {formData.name}
            </p>
            <p className="text-gray-600 text-sm">
              <strong>Email:</strong> {formData.email}
            </p>
            <p className="text-gray-600 text-sm">
              <strong>Teléfono:</strong> {formData.phone}
            </p>
            {formData.company && (
              <p className="text-gray-600 text-sm">
                <strong>Empresa:</strong> {formData.company}
              </p>
            )}
            {formData.message && (
              <p className="text-gray-600 text-sm">
                <strong>Mensaje:</strong> {formData.message}
              </p>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8">
            <button
              onClick={() => handleNavigate('/productos')}
              disabled={navigating}
              className={`flex-1 py-2.5 sm:py-3 bg-blue-600 text-white rounded-md ${
                navigating ? 'opacity-75 cursor-wait' : 'hover:bg-blue-700'
              } transition-colors flex items-center justify-center gap-2 text-sm sm:text-base`}
            >
              <ShoppingBag className="h-4 w-4 sm:h-5 sm:w-5" />
              Explorar Más Productos
            </button>
            <button
              onClick={() => handleNavigate('/')}
              disabled={navigating}
              className={`flex-1 py-2.5 sm:py-3 bg-gray-100 text-gray-800 rounded-md ${
                navigating ? 'opacity-75 cursor-wait' : 'hover:bg-gray-200'
              } transition-colors flex items-center justify-center gap-2 text-sm sm:text-base`}
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              Volver al Inicio
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}